import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  fetchBranches,
  createBranch,
  updateBranch,
  deleteBranch,
} from "@/store/altingross/branches";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Loader2,
  MapPin, Phone, Star,
} from "lucide-react";
import { toast } from "sonner";

// ─── Validation ──────────────────────────────────────────────────────────────
const schema = Yup.object({
  name:      Yup.string().trim().min(2, "En az 2 karakter").required("Zorunlu"),
  address:   Yup.string().trim().required("Zorunlu"),
  phone:     Yup.string().trim().required("Zorunlu"),
  email:     Yup.string().trim().email("Geçerli e-posta girin").required("Zorunlu"),
  latitude:  Yup.string().trim(),
  longitude: Yup.string().trim(),
  order:     Yup.number().min(0),
  isActive:  Yup.boolean(),
  isCenter:  Yup.boolean(),
});

const EMPTY = {
  name: "", address: "", phone: "", email: "",
  latitude: "", longitude: "", order: 0,
  isActive: true, isCenter: false,
};
const PAGE_SIZE = 20;

export default function Branches() {
  const dispatch = useDispatch();
  const { list, loading, pagination, savingId, deletingId } = useSelector(
    (s) => s.branches
  );

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    (p = page) => dispatch(fetchBranches({ page: p, size: PAGE_SIZE })),
    [dispatch, page]
  );

  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  // ─── Form ──────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: EMPTY,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        name:      values.name.trim(),
        address:   values.address.trim(),
        phone:     values.phone.trim(),
        email:     values.email.trim(),
        latitude:  values.latitude.trim(),
        longitude: values.longitude.trim(),
        order:     Number(values.order),
        isActive:  values.isActive,
        isCenter:  values.isCenter,
      };

      let res;
      if (editing) {
        res = await dispatch(updateBranch({ id: editing.id, data: payload }));
        if (!res.error) {
          toast.success("Şube güncellendi.");
          handleClose();
          load(page);
        }
      } else {
        res = await dispatch(createBranch(payload));
        if (!res.error) {
          toast.success("Şube oluşturuldu.");
          handleClose();
          load(1);
          setPage(1);
        }
      }
    },
  });

  const handleOpen = (branch = null) => {
    setEditing(branch);
    formik.resetForm({
      values: branch ? {
        name:      branch.name ?? "",
        address:   branch.address ?? "",
        phone:     branch.phone ?? "",
        email:     branch.email ?? "",
        latitude:  branch.latitude ?? "",
        longitude: branch.longitude ?? "",
        order:     branch.order ?? 0,
        isActive:  branch.isActive ?? true,
        isCenter:  branch.isCenter ?? false,
      } : EMPTY,
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditing(null);
    formik.resetForm({ values: EMPTY });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await dispatch(deleteBranch(deleteTarget.id));
    if (!res.error) {
      toast.success("Şube silindi.");
      load(page);
    }
    setDeleteTarget(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        testId="branches-header"
        title="Şube Tanımları"
        description="İşletme şubelerini yönetin."
        action={
          <Button size="sm" onClick={() => handleOpen()}>
            <Plus className="w-4 h-4 mr-1" />
            Yeni Ekle
          </Button>
        }
      />

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Kolon başlıkları */}
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center border-b border-gray-100 px-4 py-2.5 gap-4">
          <span className="text-xs font-semibold text-black uppercase tracking-wide">Şube</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden md:block">Telefon</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block">Durum</span>
          <span></span>
        </div>

        {loading && list.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Yükleniyor…
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            Henüz şube eklenmedi.
          </div>
        ) : (
          <ul>
            {list.map((b, idx) => (
                <li
                  key={b.id}
                  className={`grid grid-cols-[1fr_auto_auto_auto] items-center px-4 py-3 gap-4 ${
                    idx !== list.length - 1 ? "border-b border-gray-100" : ""
                  } hover:bg-gray-50 transition-colors`}
                >
                  {/* Şube adı + adres */}
                  <span className="flex flex-col min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800 truncate">
                      {b.isCenter && (
                        <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-400 flex-shrink-0" title="Merkez Şube" />
                      )}
                      {b.name}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400 truncate mt-0.5">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {b.address}
                    </span>
                  </span>

                  {/* Telefon */}
                  <span className="hidden md:flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                    <Phone className="w-3 h-3 text-gray-400" />
                    {b.phone}
                  </span>

                  {/* Durum */}
                  <span className="hidden sm:block">
                    {b.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700">Aktif</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500">Pasif</span>
                    )}
                  </span>

                  {/* Aksiyonlar */}
                  <span className="flex items-center gap-1">
                    {deletingId === b.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                    ) : (
                      <>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-gray-700"
                          onClick={() => handleOpen(b)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-red-500"
                          onClick={() => setDeleteTarget(b)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </span>
                </li>
              ))}
          </ul>
        )}

        {/* Sayfalama */}
        {list.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
            <span>
              Toplam {pagination.count} şube — Sayfa {pagination.page}/{pagination.totalPages || 1}
            </span>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Önceki
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}>
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Oluştur / Düzenle Dialog ──────────────────────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Şubeyi Düzenle" : "Yeni Şube"}</DialogTitle>
            <DialogDescription>
              {editing ? `"${editing.name}" şubesini düzenliyorsunuz.` : "Yeni bir şube ekleyin."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4 pt-1">
            {/* Ad */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="br-name">Şube Adı *</Label>
              <Input id="br-name" name="name" placeholder="Merkez Şube"
                value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.name && formik.errors.name && (
                <p className="text-xs text-red-500">{formik.errors.name}</p>
              )}
            </div>

            {/* Adres */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="br-address">Adres *</Label>
              <Input id="br-address" name="address" placeholder="Cadde, No, İlçe, Şehir"
                value={formik.values.address} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.address && formik.errors.address && (
                <p className="text-xs text-red-500">{formik.errors.address}</p>
              )}
            </div>

            {/* Telefon + E-posta */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="br-phone">Telefon *</Label>
                <Input id="br-phone" name="phone" placeholder="+90 555 000 00 00"
                  value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                {formik.touched.phone && formik.errors.phone && (
                  <p className="text-xs text-red-500">{formik.errors.phone}</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="br-email">E-posta *</Label>
                <Input id="br-email" name="email" type="email" placeholder="sube@altingross.com"
                  value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-xs text-red-500">{formik.errors.email}</p>
                )}
              </div>
            </div>

            {/* Koordinatlar */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="br-lat">
                  Enlem <span className="text-gray-400 font-normal">(Latitude)</span>
                </Label>
                <Input id="br-lat" name="latitude" placeholder="41.015137"
                  value={formik.values.latitude} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="br-lng">
                  Boylam <span className="text-gray-400 font-normal">(Longitude)</span>
                </Label>
                <Input id="br-lng" name="longitude" placeholder="28.979530"
                  value={formik.values.longitude} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              </div>
            </div>

            {/* Sıra */}
            <div className="flex flex-col gap-1.5 w-32">
              <Label htmlFor="br-order">Sıra</Label>
              <Input id="br-order" name="order" type="number" min={0}
                value={formik.values.order} onChange={formik.handleChange} onBlur={formik.handleBlur} />
            </div>

            {/* Aktif + Merkez */}
            <div className="flex items-center gap-6 pt-1 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <Switch id="br-active" checked={formik.values.isActive}
                  onCheckedChange={(v) => formik.setFieldValue("isActive", v)} />
                <Label htmlFor="br-active" className="cursor-pointer text-sm">Aktif</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="br-center" checked={formik.values.isCenter}
                  onCheckedChange={(v) => formik.setFieldValue("isCenter", v)} />
                <Label htmlFor="br-center" className="cursor-pointer text-sm flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-yellow-500" />
                  Merkez Şube
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>İptal</Button>
              <Button type="submit" disabled={savingId !== null || formik.isSubmitting}>
                {savingId !== null
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Kaydediliyor…</>
                  : editing ? "Güncelle" : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Sil Onayı ─────────────────────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Şubeyi sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> şubesi kalıcı olarak silinecek. Emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={handleDelete}>
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
